import type { ReaderOptions } from 'zxing-wasm/reader';

export const SCANNER_BARCODE_FORMATS: NonNullable<ReaderOptions['formats']> = [
  'QRCode',
  'DataMatrix',
  'Aztec',
  'PDF417',
  'Code128',
  'Code39',
  'Code93',
  'EAN-13',
  'EAN-8',
  'UPC-A',
  'UPC-E',
  'Codabar',
];

export const SCANNER_READER_OPTIONS: ReaderOptions = {
  formats: SCANNER_BARCODE_FORMATS,
  tryHarder: true,
  tryRotate: true,
  tryInvert: true,
  maxNumberOfSymbols: 255,
  textMode: 'Plain',
};

// These 1D symbology roots include their documented variants (for example, ITF includes ITF-14).
// Keep them out of the normal pass to preserve its current performance and recognition profile.
export const EXTENDED_LINEAR_BARCODE_FORMATS: NonNullable<ReaderOptions['formats']> = [
  'Code32',
  'PZN',
  'ITF',
  'DataBar',
  'Telepen',
];

export const EXTENDED_SCANNER_READER_OPTIONS: ReaderOptions = {
  ...SCANNER_READER_OPTIONS,
  // Retain all usual 1D and 2D formats while briefly trying extra 1D symbologies.
  formats: [...SCANNER_BARCODE_FORMATS, ...EXTENDED_LINEAR_BARCODE_FORMATS],
};

export type ScannerReaderMode = 'normal' | 'extended';

export const NORMAL_DECODE_ATTEMPT_LIMIT = 5;
export const EXTENDED_DECODE_ATTEMPT_LIMIT = 3;

/**
 * Alternates decoder formats only after completed, unconfirmed decode attempts.
 * It never controls the camera or the WASM module, so changing mode does not restart either.
 */
export class ScannerReaderOptionsFallback {
  private currentMode: ScannerReaderMode = 'normal';
  private attemptsInCurrentMode = 0;

  get mode(): ScannerReaderMode {
    return this.currentMode;
  }

  get options(): ReaderOptions {
    return this.currentMode === 'normal'
      ? SCANNER_READER_OPTIONS
      : EXTENDED_SCANNER_READER_OPTIONS;
  }

  reset(): void {
    this.currentMode = 'normal';
    this.attemptsInCurrentMode = 0;
  }

  recordDecodeAttempt(confirmedBarcode: boolean): void {
    if (confirmedBarcode) {
      this.reset();
      return;
    }

    this.attemptsInCurrentMode++;
    const limit = this.currentMode === 'normal'
      ? NORMAL_DECODE_ATTEMPT_LIMIT
      : EXTENDED_DECODE_ATTEMPT_LIMIT;
    if (this.attemptsInCurrentMode < limit) {
      return;
    }

    this.currentMode = this.currentMode === 'normal' ? 'extended' : 'normal';
    this.attemptsInCurrentMode = 0;
  }
}
